import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext.jsx';
import { FaPencilAlt, FaTrash, FaUpload, FaPlus, FaSearch, FaFilter, FaBox, FaCog, FaBarcode, FaEye, FaTimes, FaDollarSign, FaShoppingCart, FaChartLine, FaClock, FaArrowUp, FaArrowDown, FaSave, FaCheck } from 'react-icons/fa';
import * as XLSX from 'xlsx';

// Simple Daily Stats Component
const DailySalesStats = () => {
  const [dailyStats, setDailyStats] = useState({
    totalRevenue: 0,
    totalTransactions: 0,
    averageTransaction: 0,
    totalItemsSold: 0,
    topSellingItem: 'N/A',
    growthPercentage: 0
  });
  const [loading, setLoading] = useState(false);

  const fetchDailyStats = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const [todayResponse, yesterdayResponse] = await Promise.all([
        axios.get(`${import.meta.env.VITE_API_URL}/api/pos-transactions`, {
          params: { startDate: today, endDate: today }
        }),
        axios.get(`${import.meta.env.VITE_API_URL}/api/pos-transactions`, {
          params: { startDate: yesterday, endDate: yesterday }
        })
      ]);

      const todayTransactions = Array.isArray(todayResponse.data) 
        ? todayResponse.data 
        : todayResponse.data.transactions || [];
      
      const yesterdayTransactions = Array.isArray(yesterdayResponse.data) 
        ? yesterdayResponse.data 
        : yesterdayResponse.data.transactions || [];

      const totalRevenue = todayTransactions.reduce((sum, t) => sum + (t.totalAmount || 0), 0);
      const yesterdayRevenue = yesterdayTransactions.reduce((sum, t) => sum + (t.totalAmount || 0), 0);
      const totalTransactions = todayTransactions.length;
      const averageTransaction = totalTransactions ? totalRevenue / totalTransactions : 0;
      const growthPercentage = yesterdayRevenue ? ((totalRevenue - yesterdayRevenue) / yesterdayRevenue) * 100 : 0;

      const itemCounts = {};
      let totalItemsSold = 0;

      todayTransactions.forEach(transaction => {
        if (transaction.items && Array.isArray(transaction.items)) {
          transaction.items.forEach(item => {
            totalItemsSold += item.quantity || 0;
            const itemName = item.name || 'Unknown';
            itemCounts[itemName] = (itemCounts[itemName] || 0) + (item.quantity || 0);
          });
        }
      });

      const topSellingItem = Object.keys(itemCounts).length > 0 
        ? Object.entries(itemCounts).reduce((a, b) => itemCounts[a[0]] > itemCounts[b[0]] ? a : b)[0]
        : 'N/A';

      setDailyStats({
        totalRevenue,
        totalTransactions,
        averageTransaction,
        totalItemsSold,
        topSellingItem,
        growthPercentage
      });
    } catch (error) {
      console.error('Error fetching daily stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDailyStats();
    const interval = setInterval(fetchDailyStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Today's Revenue */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-gray-600 text-sm font-medium">Today's Revenue</p>
              <p className="text-xl font-bold text-emerald-600 mt-1">
                {formatCurrency(dailyStats.totalRevenue)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {dailyStats.totalTransactions} transactions
              </p>
              <div className="flex items-center gap-1 mt-2">
                {dailyStats.growthPercentage >= 0 ? (
                  <FaArrowUp className="w-3 h-3 text-green-500" />
                ) : (
                  <FaArrowDown className="w-3 h-3 text-red-500" />
                )}
                <span className={`text-xs font-medium ${dailyStats.growthPercentage >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {Math.abs(dailyStats.growthPercentage).toFixed(1)}%
                </span>
                <span className="text-xs text-gray-500">vs yesterday</span>
              </div>
            </div>
            <FaDollarSign className="w-6 h-6 text-emerald-600" />
          </div>
        </div>

        {/* Average Transaction */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-gray-600 text-sm font-medium">Average Transaction</p>
              <p className="text-xl font-bold text-blue-600 mt-1">
                {formatCurrency(dailyStats.averageTransaction)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Per sale value</p>
            </div>
            <FaShoppingCart className="w-6 h-6 text-blue-600" />
          </div>
        </div>

        {/* Items Sold */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-gray-600 text-sm font-medium">Items Sold</p>
              <p className="text-xl font-bold text-purple-600 mt-1">
                {dailyStats.totalItemsSold}
              </p>
              <p className="text-xs text-gray-500 mt-1">Total quantity</p>
            </div>
            <FaBox className="w-6 h-6 text-purple-600" />
          </div>
        </div>

        {/* Top Selling */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-gray-600 text-sm font-medium">Top Selling</p>
              <p className="text-lg font-bold text-orange-600 mt-1 truncate" title={dailyStats.topSellingItem}>
                {dailyStats.topSellingItem.length > 15 ? 
                  dailyStats.topSellingItem.substring(0, 15) + '...' : 
                  dailyStats.topSellingItem}
              </p>
              <p className="text-xs text-gray-500 mt-1">Most popular item</p>
            </div>
            <FaChartLine className="w-6 h-6 text-orange-600" />
          </div>
        </div>
      </div>
    </div>
  );
};

function POS() {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);

  // Helper to get correct auth token
  const getAuthToken = () => {
    return localStorage.getItem('adminAuthToken') || localStorage.getItem('authToken');
  };

  const [items, setItems] = useState([]);
  const [posCategories, setPosCategories] = useState([]);
  const [newItem, setNewItem] = useState({
    name: '',
    price: '',
    discount: '',
    category: '',
    section: 'Salon',
    description: '',
    sku: '',
    quantity: '',
    barcode: '',
    duration: '',
    validity: '',
  });
  const [newPosCategory, setNewPosCategory] = useState({ name: '', subBusiness: 'Salon' });
  const [editingId, setEditingId] = useState(null);
  // ✅ NEW: State for inline editing
  const [editingData, setEditingData] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('product');
  const [dataLoading, setDataLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // ✅ NEW: Filter states
  const [filterSubBusiness, setFilterSubBusiness] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadItems, setUploadItems] = useState([]);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 18;

  const generateDummyBarcode = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    return `BC${timestamp}${random}`;
  };

  const resetForm = (tab) => {
    setNewItem({
      name: '',
      price: '',
      discount: '',
      category: '',
      section: 'Salon',
      description: '',
      sku: tab === 'product' ? '' : undefined,
      quantity: tab === 'product' ? '' : undefined,
      barcode: tab === 'product' ? '' : undefined,
      duration: tab === 'service' ? '' : undefined,
      validity: tab === 'subscription' ? '' : undefined,
    });
    setEditingId(null);
    setEditingData(null); // ✅ NEW: Clear inline editing data
    setError('');
  };

  useEffect(() => {
    const fetchData = async () => {
      console.log('Axios default Authorization header:', axios.defaults.headers.common['Authorization']);
      setDataLoading(true);
      try {
        const [posRes, categoriesRes] = await Promise.all([
          axios.get(`${import.meta.env.VITE_API_URL}/api/pos`),
          axios.get(`${import.meta.env.VITE_API_URL}/api/categories`), // ✅ UNIFIED ENDPOINT
        ]);
        console.log('POS data fetched successfully');
        setItems(Array.isArray(posRes.data) ? posRes.data : []);
        setPosCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
        setError('');
      } catch (err) {
        console.error('Failed to load POS data:', err.response?.data || err.message);
        setError(`Failed to load data: ${err.response?.data?.message || err.message}`);
        if (err.response?.status === 401) {
          logout();
          navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
        }
      } finally {
        setDataLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleAddItem = async () => {
    if (!newItem.name || newItem.price <= 0) {
      setError('Name and Price are required and price must be greater than 0.');
      return;
    }
    try {
      const selectedCategory = posCategories.find(cat => cat.name === newItem.category);
      const payload = {
        type: activeTab,
        name: newItem.name,
        price: newItem.price,
        discount: newItem.discount || 0,
        category: selectedCategory ? selectedCategory._id : undefined,
        section: newItem.section,
        subBusiness: newItem.section,
        description: newItem.description || undefined,
        sku: activeTab === 'product' ? newItem.sku || undefined : undefined,
        quantity: activeTab === 'product' ? newItem.quantity || undefined : undefined,
        barcode: activeTab === 'product' ? newItem.barcode || generateDummyBarcode() : undefined,
        duration: activeTab === 'service' ? newItem.duration || undefined : undefined,
        validity: activeTab === 'subscription' ? newItem.validity || undefined : undefined,
      };
      const res = await axios.post(`${import.meta.env.VITE_API_URL}/api/pos`, payload);
      setItems([...items, res.data]);
      resetForm(activeTab);
    } catch (err) {
      console.error('Failed to add item:', err.response?.data || err.message);
      setError(`Failed to add item: ${err.response?.data?.message || err.message}`);
      if (err.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
      }
    }
  };

  const handleUpdateItem = async () => {
    if (!newItem.name || newItem.price <= 0) {
      setError('Name and Price are required and price must be greater than 0.');
      return;
    }
    try {
      const selectedCategory = posCategories.find(cat => cat.name === newItem.category);
      const payload = {
        type: activeTab,
        name: newItem.name,
        price: newItem.price,
        discount: newItem.discount || 0,
        category: selectedCategory ? selectedCategory._id : undefined,
        section: newItem.section,
        subBusiness: newItem.section,
        description: newItem.description || undefined,
        sku: activeTab === 'product' ? newItem.sku || undefined : undefined,
        quantity: activeTab === 'product' ? newItem.quantity || undefined : undefined,
        barcode: activeTab === 'product' ? newItem.barcode || generateDummyBarcode() : undefined,
        duration: activeTab === 'service' ? newItem.duration || undefined : undefined,
        validity: activeTab === 'subscription' ? newItem.validity || undefined : undefined,
      };
      const res = await axios.put(`${import.meta.env.VITE_API_URL}/api/pos/${editingId}`, payload);
      setItems(items.map(item => item._id === editingId ? res.data : item));
      resetForm(activeTab);
    } catch (err) {
      console.error('Failed to update item:', err.response?.data || err.message);
      setError(`Failed to update item: ${err.response?.data?.message || err.message}`);
      if (err.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
      }
    }
  };

  // ✅ NEW: Inline editing functions
  const handleStartEdit = (item) => {
    setEditingId(item._id);
    setEditingData({
      name: item.name,
      price: item.price,
      discount: item.discount || 0,
      category: item.category?._id || '',
      section: item.section || item.subBusiness,
      description: item.description || '',
      sku: item.sku || '',
      quantity: item.quantity || 0,
      barcode: item.barcode || '',
      duration: item.duration || '',
      validity: item.validity || '',
      type: item.type,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData(null);
  };

  const handleSaveEdit = async (itemId) => {
    if (!editingData.name || editingData.price <= 0) {
      setError('Name and Price are required and price must be greater than 0.');
      return;
    }
    
    try {
      const payload = {
        type: editingData.type,
        name: editingData.name,
        price: editingData.price,
        discount: editingData.discount || 0,
        category: editingData.category || undefined,
        section: editingData.section,
        subBusiness: editingData.section,
        description: editingData.description || undefined,
        sku: editingData.type === 'product' ? editingData.sku : undefined,
        quantity: editingData.type === 'product' ? editingData.quantity : undefined,
        barcode: editingData.type === 'product' ? editingData.barcode : undefined,
        duration: editingData.type === 'service' ? editingData.duration : undefined,
        validity: editingData.type === 'subscription' ? editingData.validity : undefined,
      };
      
      const res = await axios.put(`${import.meta.env.VITE_API_URL}/api/pos/${itemId}`, payload);
      setItems(items.map(item => item._id === itemId ? res.data : item));
      setEditingId(null);
      setEditingData(null);
      setError('');
    } catch (err) {
      console.error('Failed to update item:', err.response?.data || err.message);
      setError(`Failed to update item: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleDeleteItem = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete ${name}?`)) return;
    try {
      await axios.delete(`${import.meta.env.VITE_API_URL}/api/pos/${id}`);
      setItems(items.filter(item => item._id !== id));
      setSelectedItems(selectedItems.filter(selectedId => selectedId !== id));
      setError('');
    } catch (err) {
      console.error('Failed to delete item:', err.response?.data || err.message);
      setError(`Failed to delete item: ${err.response?.data?.message || err.message}`);
      if (err.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      setError('No items selected for deletion.');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedItems.length} selected items?`)) return;
    try {
      await Promise.all(
        selectedItems.map(id =>
          axios.delete(`${import.meta.env.VITE_API_URL}/api/pos/${id}`)
        )
      );
      setItems(items.filter(item => !selectedItems.includes(item._id)));
      setSelectedItems([]);
      setError('');
    } catch (err) {
      console.error('Failed to bulk delete items:', err.response?.data || err.message);
      setError(`Failed to bulk delete items: ${err.response?.data?.message || err.message}`);
      if (err.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
      }
    }
  };

  const handleAddPosCategory = async () => {
  if (!newPosCategory.name.trim()) {
    setError('Category name is required.');
    return;
  }
  try {
    // ✅ FIXED: Use unified /api/categories endpoint
    const res = await axios.post(`${import.meta.env.VITE_API_URL}/api/categories`, newPosCategory);
    setPosCategories([...posCategories, res.data]);
    setNewPosCategory({ name: '', subBusiness: 'Salon' });
    setError('');
  } catch (err) {
    console.error('Failed to add POS category:', err);
    setError(`Failed to add POS category: ${err.response?.data?.message || err.message}`);
    if (err.response?.status === 401) {
      logout();
      navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
    }
  }
};

  const handleUpdateCategory = async () => {
  if (!editingCategory.name || !editingCategory.subBusiness) {
    setError('Category name and subBusiness are required.');
    return;
  }
  try {
    // ✅ FIXED: Use unified /api/categories endpoint
    const res = await axios.put(`${import.meta.env.VITE_API_URL}/api/categories/${editingCategory._id}`, {
      name: editingCategory.name,
      subBusiness: editingCategory.subBusiness,
    });
    setPosCategories(posCategories.map(cat => cat._id === editingCategory._id ? res.data : cat));
    setEditingCategory(null);
    setError('');
  } catch (err) {
    console.error('Failed to update category:', err);
    setError(`Failed to update category: ${err.response?.data?.message || err.message}`);
    if (err.response?.status === 401) {
      logout();
      navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
    }
  }
};

  const handleDeleteCategory = async (id, name) => {
  if (!window.confirm(`Are you sure you want to delete the category "${name}"?`)) return;
  try {
    // ✅ FIXED: Use unified /api/categories endpoint
    await axios.delete(`${import.meta.env.VITE_API_URL}/api/categories/${id}`);
    setPosCategories(posCategories.filter(cat => cat._id !== id));
    setError('');
  } catch (err) {
    console.error('Failed to delete category:', err);
    // ✅ BETTER ERROR: Show usage info if category is in use
    const errorMessage = err.response?.data?.message || err.message;
    if (err.response?.data?.usage) {
      const { inventory, pos, total } = err.response.data.usage;
      setError(`${errorMessage}\n\nUsage breakdown:\n- Inventory: ${inventory} items\n- POS: ${pos} items`);
    } else {
      setError(`Failed to delete category: ${errorMessage}`);
    }
    if (err.response?.status === 401) {
      logout();
      navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
    }
  }
};

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    resetForm(tab);
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === filteredItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredItems.map(item => item._id));
    }
  };

  const handleEditItem = (item) => {
    setEditingId(item._id);
    setActiveTab(item.type);
    setNewItem({
      name: item.name || '',
      price: item.price || '',
      discount: item.discount || '',
      category: item.category?.name || '',
      section: item.section || 'Salon',
      description: item.description || '',
      sku: item.sku || '',
      quantity: item.quantity || '',
      barcode: item.barcode || '',
      duration: item.duration || '',
      validity: item.validity || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const fileReader = new FileReader();
      fileReader.onload = async (event) => {
        const data = event.target.result;
        let itemsToUpload = [];
        let errors = [];

        if (file.name.endsWith('.csv')) {
          const text = new TextDecoder().decode(data);
          const rows = text.split('\n').map(row => row.split(',').map(cell => cell.trim()));
          const headers = rows[0].map(h => h.toLowerCase().trim().replace(/[^a-z0-9]/g, ''));
          itemsToUpload = rows.slice(1).map(row => {
            const item = {};
            headers.forEach((header, index) => {
              item[header] = row[index]?.toString().trim();
            });
            return item;
          });
        } else if (file.name.endsWith('.xlsx')) {
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          const headers = jsonData[0].map(h => h.toString().toLowerCase().trim().replace(/[^a-z0-9]/g, ''));
          itemsToUpload = jsonData.slice(1).map(row => {
            const item = {};
            headers.forEach((header, index) => {
              item[header] = row[index]?.toString().trim();
            });
            return item;
          });
        } else {
          setError('Unsupported file format. Please upload a CSV or XLSX file.');
          return;
        }

        const validItems = [];
        const existingItems = new Map(items.map(item => [item.sku?.toLowerCase(), item]));
        itemsToUpload.forEach((item, index) => {
          const itemName = item.name || `Row ${index + 2}`;
          const missingFields = [];
          if (!item.name) missingFields.push('name');
          if (!item.price || isNaN(parseFloat(item.price)) || parseFloat(item.price) <= 0) missingFields.push('price');
          if (!item.type || !['product', 'service', 'subscription'].includes(item.type)) {
            missingFields.push('type (must be product, service, or subscription)');
          }
          if (item.type === 'product' && !item.sku) missingFields.push('sku');
          if (item.section && !['Salon', 'Spa', 'Gym'].includes(item.section)) {
            missingFields.push(`section (must be Salon, Spa, or Gym)`);
          }
          if (item.category && !posCategories.some(cat => cat.name === item.category)) {
            missingFields.push(`category (must match existing POS category)`);
          }
          if (item.sku && existingItems.has(item.sku.toLowerCase())) {
            errors.push(`Row ${index + 2} (${itemName}): Duplicate SKU "${item.sku}"`);
            return;
          }
          if (missingFields.length > 0) {
            errors.push(`Row ${index + 2} (${itemName}): Missing or invalid fields: ${missingFields.join(', ')}`);
            return;
          }

          const selectedCategory = posCategories.find(cat => cat.name === item.category);
          validItems.push({
            type: item.type,
            name: item.name,
            price: parseFloat(item.price),
            discount: item.discount ? parseFloat(item.discount) : 0,
            category: selectedCategory ? selectedCategory._id : undefined,
            section: item.section || 'Salon',
            subBusiness: item.section || 'Salon',
            description: item.description || undefined,
            sku: item.type === 'product' ? item.sku || undefined : undefined,
            quantity: item.type === 'product' ? (item.quantity ? parseInt(item.quantity) : undefined) : undefined,
            barcode: item.type === 'product' ? (item.barcode || generateDummyBarcode()) : undefined,
            duration: item.type === 'service' ? (item.duration || undefined) : undefined,
            validity: item.type === 'subscription' ? (item.validity || undefined) : undefined,
          });
        });

        if (validItems.length === 0) {
          setError('No valid items found in the uploaded file.');
          setUploadItems([]);
          setUploadErrors(errors);
          setShowUploadModal(true);
          return;
        }

        setUploadItems(validItems);
        setUploadErrors(errors);
        setShowUploadModal(true);
      };

      if (file.name.endsWith('.csv')) {
        fileReader.readAsText(file);
      } else {
        fileReader.readAsArrayBuffer(file);
      }
    } catch (err) {
      console.error('Bulk upload error:', err);
      setError(`Bulk upload failed: ${err.message}`);
    }
  };

  const handleConfirmUpload = async () => {
    if (uploadItems.length === 0) {
      setUploadErrors(['No valid items to upload']);
      return;
    }
    setUploadLoading(true);
    try {
      const responses = await Promise.all(
        uploadItems.map(item =>
          axios.post(`${import.meta.env.VITE_API_URL}/api/pos`, item).catch(err => ({
            error: `Failed to upload item "${item.name}": ${err.response?.data?.message || err.message}`,
          }))
        )
      );
      const newItems = responses.filter(res => !res.error).map(res => res.data);
      const newErrors = responses.filter(res => res.error).map(res => res.error);
      setItems([...items, ...newItems]);
      setUploadErrors([...uploadErrors, ...newErrors]);
      if (newErrors.length === 0) {
        setShowUploadModal(false);
        setUploadItems([]);
        setError('');
      } else {
        setError('Some items failed to upload');
      }
    } catch (err) {
      console.error('Bulk upload error:', err);
      setUploadErrors([...uploadErrors, `Bulk upload failed: ${err.message}`]);
    } finally {
      setUploadLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getSectionColor = (section) => {
    switch (section) {
      case 'Salon': return 'bg-pink-100 text-pink-800';
      case 'Spa': return 'bg-teal-100 text-teal-800';
      case 'Gym': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  
  // ✅ UPDATED: Enhanced filtering logic
  const filteredItems = items.filter(item => {
    // Search filter (existing)
    const matchesSearch = searchQuery === '' ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.category?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.section?.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (!matchesSearch) return false;

    // ✅ NEW: Sub-business filter
    if (filterSubBusiness && item.section !== filterSubBusiness) {
      return false;
    }
    
    // ✅ NEW: Category filter
    if (filterCategory && (!item.category || item.category._id !== filterCategory)) {
      return false;
    }
    
    return true;
  });

  // Get unique sub-businesses for filter dropdown
  const subBusinesses = [...new Set(items.map(item => item.section).filter(Boolean))];
  
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">POS Management</h1>
          <p className="text-gray-600">Manage your products, services, and subscriptions.</p>
        </div>

        {/* Daily Sales Stats */}
        <DailySalesStats />

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <p>{error}</p>
          </div>
        )}

        {/* Add/Edit Item Form */}
        <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4">
            <div className="flex items-center">
              <FaPlus className="text-white mr-3" />
              <h2 className="text-xl font-semibold text-white">{editingId ? 'Edit Item' : 'Add New Item'}</h2>
            </div>
          </div>

          <div className="p-6">
            <div className="flex mb-6 space-x-2">
              {['product', 'service', 'subscription'].map(tab => (
                <button
                  key={tab}
                  className={`px-4 py-2 rounded-lg font-medium capitalize ${
                    activeTab === tab
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Enter item name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price (₦) *</label>
                <input
                  type="number"
                  value={newItem.price}
                  onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || '' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Discount (%)</label>
                <input
                  type="number"
                  value={newItem.discount}
                  onChange={(e) => setNewItem({ ...newItem, discount: parseFloat(e.target.value) || '' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={newItem.category}
                  onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="">Select Category</option>
                  {posCategories.map(cat => (
                    <option key={cat._id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Section</label>
                <select
                  value={newItem.section}
                  onChange={(e) => setNewItem({ ...newItem, section: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="Salon">Salon</option>
                  <option value="Spa">Spa</option>
                  <option value="Gym">Gym</option>
                </select>
              </div>

              {activeTab === 'product' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SKU</label>
                    <input
                      type="text"
                      value={newItem.sku}
                      onChange={(e) => setNewItem({ ...newItem, sku: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="Enter SKU"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
                    <input
                      type="number"
                      value={newItem.quantity}
                      onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || '' })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Barcode</label>
                    <input
                      type="text"
                      value={newItem.barcode}
                      onChange={(e) => setNewItem({ ...newItem, barcode: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="Enter barcode"
                    />
                  </div>
                </>
              )}

              {activeTab === 'service' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
                  <input
                    type="text"
                    value={newItem.duration}
                    onChange={(e) => setNewItem({ ...newItem, duration: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="e.g. 1 hr or 30 mins"
                  />
                </div>
              )}

              {activeTab === 'subscription' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Validity (days)</label>
                  <input
                    type="number"
                    value={newItem.validity}
                    onChange={(e) => setNewItem({ ...newItem, validity: parseInt(e.target.value) || '' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Enter validity"
                  />
                </div>
              )}

              <div className="md:col-span-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <input
                  type="text"
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Enter description"
                />
              </div>
            </div>

            <div className="mt-6 flex space-x-3">
              <button
                onClick={editingId ? handleUpdateItem : handleAddItem}
                className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center"
              >
                <FaPlus className="mr-2" />
                {editingId ? 'Update Item' : 'Add Item'}
              </button>
              {editingId && (
                <button
                  onClick={() => resetForm(activeTab)}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Manage POS Categories */}
        <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <FaCog className="text-emerald-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-800">Manage POS Categories</h2>
            </div>
            <button
              onClick={() => setShowCategoriesModal(true)}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center"
            >
              <FaEye className="mr-2" />
              View Categories
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category Name</label>
              <input
                type="text"
                value={editingCategory ? editingCategory.name : newPosCategory.name}
                onChange={(e) => editingCategory
                  ? setEditingCategory({ ...editingCategory, name: e.target.value })
                  : setNewPosCategory({ ...newPosCategory, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Enter category name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sub-Business</label>
              <select
                value={editingCategory ? editingCategory.subBusiness : newPosCategory.subBusiness}
                onChange={(e) => editingCategory
                  ? setEditingCategory({ ...editingCategory, subBusiness: e.target.value })
                  : setNewPosCategory({ ...newPosCategory, subBusiness: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="Salon">Salon</option>
                <option value="Spa">Spa</option>
                <option value="Gym">Gym</option>
              </select>
            </div>
          </div>
          <button
            onClick={editingCategory ? handleUpdateCategory : handleAddPosCategory}
            className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center"
          >
            <FaPlus className="mr-2" />
            {editingCategory ? 'Update Category' : 'Add Category'}
          </button>
          {editingCategory && (
            <button
              onClick={() => setEditingCategory(null)}
              className="ml-3 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Cancel
            </button>
          )}
        </div>

        {/* POS Items List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FaBox className="text-white mr-3" />
                <h2 className="text-xl font-semibold text-white">POS Items ({filteredItems.length})</h2>
              </div>
              {selectedItems.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
                >
                  <FaTrash className="mr-2" />
                  Delete Selected ({selectedItems.length})
                </button>
              )}
            </div>
          </div>

          <div className="p-6">
            {/* ✅ NEW: Enhanced Filter Section */}
            <div className="mb-6 bg-gray-50 rounded-lg border border-gray-200 p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Search Bar */}
                <div className="relative md:col-span-2">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Search by name, category, or section..."
                  />
                </div>

                {/* Sub-Business Filter */}
                <select
                  value={filterSubBusiness}
                  onChange={(e) => {
                    setFilterSubBusiness(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                >
                  <option value="">All Sub-Businesses</option>
                  {subBusinesses.map(sb => (
                    <option key={sb} value={sb}>{sb}</option>
                  ))}
                </select>

                {/* Category Filter */}
                <select
                  value={filterCategory}
                  onChange={(e) => {
                    setFilterCategory(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                >
                  <option value="">All Categories</option>
                  {posCategories.map(cat => (
                    <option key={cat._id} value={cat._id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Filter Info & Clear Button */}
              <div className="mt-3 flex justify-between items-center">
                <span className="text-sm text-gray-600 font-medium">
                  Showing {filteredItems.length} of {items.length} items
                </span>
                {(searchQuery || filterSubBusiness || filterCategory) && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setFilterSubBusiness('');
                      setFilterCategory('');
                      setCurrentPage(1);
                    }}
                    className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center"
                  >
                    <FaTimes className="mr-1" />
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedItems.length === filteredItems.length && filteredItems.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="ml-2 text-sm text-gray-600">
                  Select All ({filteredItems.length} items)
                </span>
              </label>
            </div>

            {dataLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-12">
                <FaBox className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-500">No items found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentItems.map((item) => (
                  <div
                    key={item._id}
                    className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
                  >
                    {/* ✅ CONDITIONAL: Edit Mode vs View Mode */}
                    {editingId === item._id && editingData ? (
                      // 🔴 EDIT MODE - Inline editing
                      <div className="space-y-3">
                        {/* Name Input */}
                        <input
                          type="text"
                          value={editingData.name}
                          onChange={(e) => setEditingData({ ...editingData, name: e.target.value })}
                          className="w-full px-3 py-2 border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 font-medium text-sm"
                          placeholder="Item Name"
                        />

                        {/* Price & Discount */}
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            value={editingData.price}
                            onChange={(e) => setEditingData({ ...editingData, price: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                            placeholder="Price"
                          />
                          <input
                            type="number"
                            value={editingData.discount}
                            onChange={(e) => setEditingData({ ...editingData, discount: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                            placeholder="Discount %"
                          />
                        </div>

                        {/* Section & Category */}
                        <div className="grid grid-cols-2 gap-2">
                          <select
                            value={editingData.section}
                            onChange={(e) => setEditingData({ ...editingData, section: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                          >
                            <option value="Salon">Salon</option>
                            <option value="Spa">Spa</option>
                            <option value="Gym">Gym</option>
                          </select>

                          <select
                            value={editingData.category}
                            onChange={(e) => setEditingData({ ...editingData, category: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                          >
                            <option value="">No Category</option>
                            {posCategories.map(cat => (
                              <option key={cat._id} value={cat._id}>{cat.name}</option>
                            ))}
                          </select>
                        </div>

                        {/* Conditional fields based on type */}
                        {item.type === 'product' && (
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={editingData.sku}
                              onChange={(e) => setEditingData({ ...editingData, sku: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                              placeholder="SKU"
                            />
                            <input
                              type="number"
                              value={editingData.quantity}
                              onChange={(e) => setEditingData({ ...editingData, quantity: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                              placeholder="Quantity"
                            />
                          </div>
                        )}

                        {item.type === 'service' && (
                          <input
                            type="text"
                            value={editingData.duration}
                            onChange={(e) => setEditingData({ ...editingData, duration: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                            placeholder="Duration (e.g. 1 hr)"
                          />
                        )}

                        {item.type === 'subscription' && (
                          <input
                            type="number"
                            value={editingData.validity}
                            onChange={(e) => setEditingData({ ...editingData, validity: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                            placeholder="Validity (days)"
                          />
                        )}

                        {/* Description */}
                        <textarea
                          value={editingData.description}
                          onChange={(e) => setEditingData({ ...editingData, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                          placeholder="Description"
                          rows="2"
                        />

                        {/* Action Buttons */}
                        <div className="flex space-x-2 pt-2">
                          <button
                            onClick={() => handleSaveEdit(item._id)}
                            className="flex-1 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm flex items-center justify-center"
                          >
                            <FaCheck className="mr-2" />
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium text-sm flex items-center justify-center"
                          >
                            <FaTimes className="mr-2" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // 🟢 VIEW MODE - Normal card display
                      <>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start min-w-0 flex-1">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item._id)}
                          onChange={() => handleSelectItem(item._id)}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 mr-3 mt-1"
                        />
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-gray-900 text-sm leading-tight break-words">{item.name}</h3>
                          <p className="text-xs text-gray-500 capitalize mt-1">{item.type}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-2">
                        <button
                          onClick={() => handleStartEdit(item)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Edit inline"
                        >
                          <FaPencilAlt />
                        </button>
                        <button
                          onClick={() => handleDeleteItem(item._id, item.name)}
                          className="text-red-600 hover:text-red-800 p-1"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Price:</span>
                        <span className="font-semibold text-emerald-600">{formatPrice(item.price)}</span>
                      </div>

                      {item.discount > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Discount:</span>
                          <span className="text-orange-600">{item.discount}%</span>
                        </div>
                      )}

                      {item.category?.name && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Category:</span>
                          <span className="text-gray-900 text-sm break-words">{item.category.name}</span>
                        </div>
                      )}

                      {item.sku && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">SKU:</span>
                          <span className="text-gray-900 text-sm break-all">{item.sku}</span>
                        </div>
                      )}

                      {item.quantity !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Quantity:</span>
                          <span className="text-gray-900">{item.quantity}</span>
                        </div>
                      )}

                      {item.duration && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Duration:</span>
                          <span className="text-gray-900">{item.duration}</span>
                        </div>
                      )}

                      {item.validity && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Validity:</span>
                          <span className="text-gray-900">{item.validity} days</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-3 flex justify-between items-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSectionColor(item.section)}`}>
                        {item.section}
                      </span>
                      {item.barcode && (
                        <div className="text-xs text-gray-500 truncate max-w-24">
                          <FaBarcode className="inline mr-1" />
                          {item.barcode}
                        </div>
                      )}
                    </div>

                    {item.description && (
                      <p className="mt-2 text-sm text-gray-600 break-words line-clamp-2" title={item.description}>
                        {item.description}
                      </p>
                    )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-6 flex justify-between items-center">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Bulk Upload Section */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <FaUpload className="text-emerald-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-800">Bulk Upload Items</h2>
          </div>
          <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm text-emerald-700">
              <strong>Upload a CSV or XLSX file with columns:</strong> name, type, price, section. 
              <br />
              <strong>Optional columns:</strong> category, sku, quantity, barcode, discount, duration, validity, description.
              <br />
              <strong>Note:</strong> For products, include sku and quantity. For services, include duration. For subscriptions, include validity.
            </p>
          </div>
          <input
            type="file"
            accept=".csv,.xlsx"
            onChange={handleBulkUpload}
            className="w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
          {uploadLoading && <p className="text-emerald-600 mt-2">Uploading...</p>}
        </div>

        {/* Categories Modal */}
        {showCategoriesModal && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-800">POS Categories</h2>
                <button
                  onClick={() => setShowCategoriesModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes />
                </button>
              </div>
              {posCategories.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {posCategories.map(cat => (
                    <div
                      key={cat._id}
                      className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{cat.name}</p>
                        <p className="text-sm text-gray-500">{cat.subBusiness}</p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setEditingCategory(cat);
                            setShowCategoriesModal(false);
                          }}
                          className="text-emerald-600 hover:text-emerald-800"
                        >
                          <FaPencilAlt />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(cat._id, cat.name)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No categories found</p>
              )}
            </div>
          </div>
        )}

        {/* Upload Confirmation Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-lg max-h-[80vh] overflow-y-auto">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Confirm Bulk Upload</h2>
              {uploadErrors.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-red-600 font-medium">Errors Found:</h3>
                  <ul className="list-disc pl-5 text-red-500 text-sm">
                    {uploadErrors.map((err, index) => (
                      <li key={index}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              <h3 className="font-medium mb-2">Items to Upload: {uploadItems.length}</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 border-b text-left">Name</th>
                      <th className="p-2 border-b text-left">Type</th>
                      <th className="p-2 border-b text-left">Price</th>
                      <th className="p-2 border-b text-left">Section</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadItems.map((item, index) => (
                      <tr key={index}>
                        <td className="p-2 border-b truncate max-w-[150px]">{item.name}</td>
                        <td className="p-2 border-b capitalize">{item.type}</td>
                        <td className="p-2 border-b">{formatPrice(item.price)}</td>
                        <td className="p-2 border-b">{item.section}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadItems([]);
                    setUploadErrors([]);
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmUpload}
                  disabled={uploadLoading}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400"
                >
                  {uploadLoading ? 'Uploading...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default POS;